import { z } from 'zod';
import { DetailedSparkType } from '../spark/spark.core';

export interface CoreSparkDraft {
  _id: string;
  accountId: string;
  wizardState: Record<string, any>;
  brandId?: string;
  markets?: string[];
  detailedSparkType?: DetailedSparkType;
  groupId?: string;
  locationIds?: string[];
  name: string;
  percentComplete: number;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface UISparkDraft extends CoreSparkDraft {
  accountName?: string;
  creatorName?: string;
  creatorEmail?: string;
  lastEditorName?: string;
  // Enriched display fields (populated from lookups, not stored in DB)
  retailerLogoUrl?: string;
  retailerName?: string;
  emoji?: string;
}

export const createSparkDraftBodySchema = z.object({
  wizardState: z.record(z.any()).refine(
    (state) => {
      return typeof state === 'object' && state !== null && 'value' in state && 'context' in state;
    },
    {
      message: 'wizardState must be a valid XState snapshot with value and context',
    },
  ),
});

export const updateSparkDraftBodySchema = z.object({
  wizardState: z.record(z.any()).refine(
    (state) => {
      return typeof state === 'object' && state !== null && 'value' in state && 'context' in state;
    },
    {
      message: 'wizardState must be a valid XState snapshot with value and context',
    },
  ),
});

export const listSparkDraftsQuerySchema = z.object({
  brandId: z.string().optional(),
  detailedSparkType: z.string().optional(),
  markets: z.string().optional(),
  creatorUserId: z.string().optional(),
  search: z.string().optional(),
  retailerId: z.string().optional(),
  locationId: z.string().optional(),
  limit: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z.number().min(1).max(100).default(50),
  ),
  offset: z.preprocess((val) => (val ? Number(val) : undefined), z.number().min(0).default(0)),
  sortBy: z.enum(['createdAt', 'updatedAt', 'percentComplete']).default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export const sparkDraftPathParamsSchema = z.object({
  accountId: z.string().min(1),
  draftId: z.string().min(1),
});

export const accountIdPathParamSchema = z.object({
  accountId: z.string().min(1),
});

export type CreateSparkDraftRequest = z.infer<typeof createSparkDraftBodySchema>;
export type UpdateSparkDraftRequest = z.infer<typeof updateSparkDraftBodySchema>;
export type ListSparkDraftsQuery = z.infer<typeof listSparkDraftsQuerySchema>;
export type SparkDraftPathParams = z.infer<typeof sparkDraftPathParamsSchema>;
export type AccountIdPathParam = z.infer<typeof accountIdPathParamSchema>;

export type CreateSparkDraftResponse = { data: UISparkDraft };
export type UpdateSparkDraftResponse = { data: UISparkDraft };
export type GetSparkDraftResponse = { data: UISparkDraft };
export type DeleteSparkDraftResponse = { success: boolean };
export type CountSparkDraftsResponse = { count: number };
